import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Minus,
  Plus,
  ShoppingBag,
  Phone,
  UtensilsCrossed,
  CheckCircle2,
  AlertTriangle,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { useDishes } from "./Dishes";
import AdminBar from "./AdminBar";
import {
  useSiteContent,
  EMPTY_BLOCKED,
  isBlockedDay,
  blockedHintText,
} from "./siteContent";
import { DatePicker } from "./DatePicker";
import { fmtSlot } from "./TimeSlotSelect";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { name: "", phone: "", email: "", date: "", time: "", notes: "" };

const field =
  "w-full rounded-sm border border-[#102C1F]/25 bg-[#F4F1EB] px-4 py-2.5 text-sm text-[#102C1F] outline-none transition-colors duration-300 placeholder:text-[#102C1F]/40 focus:border-[#102C1F]";

const fieldError = field
  .replace("border-[#102C1F]/25", "border-red-500")
  .replace("focus:border-[#102C1F]", "focus:border-red-500");

const labelCls =
  "mb-1.5 block text-xs uppercase tracking-[0.2em] text-[#102C1F]/60";

export default function OrderPage() {
  const dishes = useDishes();
  const site = useSiteContent();
  const [form, setForm] = useState(EMPTY);
  const [blockedDays, setBlockedDays] = useState(EMPTY_BLOCKED);

  useEffect(() => {
    axios
      .get(`${API}/blocked-days/orders`)
      .then((r) => setBlockedDays({ ...EMPTY_BLOCKED, ...r.data }))
      .catch(() => {});
  }, []);
  const [items, setItems] = useState({});
  const [pickupSlots, setPickupSlots] = useState([]);
  const [pickupClosed, setPickupClosed] = useState(false);

  useEffect(() => {
    if (!form.date) {
      setPickupSlots([]);
      setPickupClosed(false);
      return;
    }
    axios
      .get(`${API}/slots/orders`, { params: { date: form.date } })
      .then((r) => {
        setPickupSlots(r.data.slots || []);
        setPickupClosed(!!r.data.closed);
      })
      .catch(() => setPickupSlots([]));
  }, [form.date]);
  const [sending, setSending] = useState(false);
  const [errors, setErrors] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [missingOpen, setMissingOpen] = useState(false);
  const [confirmedTotal, setConfirmedTotal] = useState(0);
  const [confirmedDate, setConfirmedDate] = useState("");
  const [confirmedTime, setConfirmedTime] = useState("");

  const set = (key) => (e) => {
    setForm({ ...form, [key]: e.target.value });
    setErrors((p) => ({ ...p, [key]: false }));
  };

  const toggle = (dish) => {
    setItems((prev) => {
      const next = { ...prev };
      if (next[dish.name]) delete next[dish.name];
      else next[dish.name] = dish.min_qty || 1;
      return next;
    });
    setErrors((p) => ({ ...p, dishes: false }));
  };

  const setQty = (name, qty) =>
    setItems((prev) => {
      const dish = dishes.find((x) => x.name === name);
      const min = dish?.min_qty || 1;
      return { ...prev, [name]: Math.max(min, Math.min(20, qty)) };
    });

  // Promotions : 3 Poulet grillé = 30,50 € — uniquement pour exactement 3
  const PROMOS = { "Poulet grillé": { qty: 3, price: 30.5 } };
  const lineTotal = (name, unit, qty) => {
    const p = PROMOS[name];
    if (!p || qty !== p.qty) return unit * qty;
    return p.price;
  };
  const total = Object.entries(items).reduce((sum, [name, qty]) => {
    const dish = dishes.find((d) => d.name === name);
    return sum + (dish ? lineTotal(name, dish.price, qty) : 0);
  }, 0);
  const regularTotal = Object.entries(items).reduce((sum, [name, qty]) => {
    const dish = dishes.find((d) => d.name === name);
    return sum + (dish ? dish.price * qty : 0);
  }, 0);
  const promoSavings = regularTotal - total;

  // Plats « 4 pers. et + » : commande au moins 24 h à l'avance
  const hasGroupDish = Object.keys(items).some(
    (n) => (dishes.find((d) => d.name === n)?.min_qty || 1) > 1
  );
  const visibleSlots = pickupSlots.filter((s) => {
    if (!hasGroupDish || !form.date) return true;
    return new Date(`${form.date}T${s}:00`).getTime() >= Date.now() + 24 * 3600 * 1000;
  });

  useEffect(() => {
    if (form.time && !visibleSlots.includes(form.time)) {
      setForm((f) => ({ ...f, time: "" }));
    }
  }, [visibleSlots, form.time]);

  const dishGroups = [];
  dishes.forEach((d) => {
    const cat = d.category || "";
    let g = dishGroups.find((x) => x.cat === cat);
    if (!g) {
      g = { cat, items: [] };
      dishGroups.push(g);
    }
    g.items.push(d);
  });

  const missingLabels = [
    errors.name && "Nom et prénom",
    errors.phone && "Téléphone",
    errors.date && "Date de retrait",
    errors.time && "Heure de retrait",
    errors.dishes && "Au moins un plat",
  ].filter(Boolean);

  const submit = async (e) => {
    e.preventDefault();
    const missing = {
      name: !form.name.trim(),
      phone: !form.phone.trim(),
      date: !form.date,
      time: !form.time,
      dishes: Object.keys(items).length === 0,
    };
    if (Object.values(missing).some(Boolean)) {
      setErrors(missing);
      setMissingOpen(true);
      return;
    }
    if (isBlockedDay(blockedDays, form.date)) {
      setErrors((p) => ({ ...p, date: true }));
      toast.error("Jour fermé — choisissez une autre date de retrait.");
      return;
    }
    setSending(true);
    try {
      const { data: created } = await axios.post(`${API}/orders`, {
        ...form,
        items: Object.entries(items).map(([name, quantity]) => ({
          name,
          price: dishes.find((d) => d.name === name).price,
          quantity,
        })),
      });
      setConfirmedTotal(created.total ?? total);
      setConfirmedDate(form.date);
      setConfirmedTime(created.time || "");
      setForm(EMPTY);
      setItems({});
      setErrors({});
      setConfirmOpen(true);
    } catch (err) {
      toast.error(
        err.response?.data?.detail ||
          "Impossible d'envoyer la commande. Réessayez."
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      data-testid="order-page"
      className="min-h-screen bg-[#F4F1EB] text-[#102C1F]"
    >
      <AdminBar />
      <header className="admin-offset fixed inset-x-0 top-0 z-50 bg-[#102C1F] text-[#F4F1EB]">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-5 py-4 md:px-10">
          <Link
            to="/"
            data-testid="order-back-btn"
            className="flex items-center gap-2 rounded-full border border-[#F4F1EB]/30 px-4 py-2 text-xs uppercase tracking-[0.2em] transition-colors duration-300 hover:bg-[#F4F1EB] hover:text-[#102C1F]"
          >
            <ArrowLeft size={14} /> Accueil
          </Link>
          <span className="font-display text-xl italic md:text-2xl">
            J'aime la vie
          </span>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-5 pb-16 pt-24 md:px-10 md:pt-28">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="mb-3 text-xs uppercase tracking-[0.3em] text-[#102C1F]/60">
            Commander
          </p>
          <h1 className="font-display text-4xl italic tracking-tight sm:text-5xl">
            {site.order_heading}
          </h1>
          <p className="mt-3 text-sm text-[#102C1F]/65 md:text-base">
            {site.order_subtext}
          </p>

          <div
            data-testid="order-info"
            className="mt-6 space-y-2 rounded-sm border border-[#102C1F]/15 bg-[#EDE8DE] p-4 text-xs leading-relaxed text-[#102C1F]/75"
          >
            <p className="flex items-center gap-2">
              <ShoppingBag size={14} className="shrink-0" />
              Commandes à emporter
            </p>
            <p className="flex items-center gap-2">
              <Phone size={14} className="shrink-0" />
              Par téléphone ou sur Internet
            </p>
            <p>
              <span className="font-medium text-[#102C1F]">
                {site.order_info_title}
              </span>
              <br />
              {(site.order_info_lines || "")
                .split("\n")
                .filter(Boolean)
                .map((l, i, arr) => (
                  <span key={i}>
                    {l}
                    {i < arr.length - 1 && <br />}
                  </span>
                ))}
            </p>
            <p className="flex items-center gap-2">
              <UtensilsCrossed size={14} className="shrink-0" />
              Plats 4 pers. et + : 24 h à l'avance
            </p>
            <p
              data-testid="order-info-note"
              className="border-t border-[#102C1F]/10 pt-3 font-display text-lg italic leading-snug text-red-700"
            >
              {site.order_info_note}
            </p>
          </div>
        </motion.div>

        {site.order_traiteur && (
          <motion.div
            data-testid="order-traiteur"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            className="mt-6 rounded-sm bg-[#102C1F] p-6 text-[#F4F1EB]"
          >
            <p className="mb-2 text-xs uppercase tracking-[0.3em] text-[#F4F1EB]/60">
              Service Traiteur
            </p>
            <p className="text-sm leading-relaxed text-[#F4F1EB]/85">
              {site.order_traiteur}
            </p>
          </motion.div>
        )}

        <motion.form
          onSubmit={submit}
          noValidate
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 space-y-5"
        >
          <div className="grid gap-5 sm:grid-cols-2">
            <label className="block">
              <span className={labelCls}>Nom et prénom</span>
              <input
                data-testid="order-name-input"
                placeholder="Votre nom et prénom"
                value={form.name}
                onChange={set("name")}
                className={errors.name ? fieldError : field}
              />
            </label>
            <label className="block">
              <span className={labelCls}>Téléphone</span>
              <input
                data-testid="order-phone-input"
                type="tel"
                placeholder="Ex : 06 12 34 56 78"
                value={form.phone}
                onChange={set("phone")}
                className={errors.phone ? fieldError : field}
              />
            </label>
            <label className="block">
              <span className={labelCls}>E-mail (facultatif)</span>
              <input
                data-testid="order-email-input"
                type="email"
                placeholder="email@exemple.fr"
                value={form.email}
                onChange={set("email")}
                className={field}
              />
            </label>
            <label className="block">
              <span className={labelCls}>Date de retrait</span>
              <DatePicker
                testid="order-date-input"
                value={form.date}
                onChange={(v) => {
                  setForm((f) => ({ ...f, date: v, time: "" }));
                  setErrors((p) => ({ ...p, date: false }));
                }}
                blocked={blockedDays}
                hasError={errors.date}
                minDaysAhead={1}
              />
              {blockedHintText(blockedDays) && (
                <p
                  data-testid="order-blocked-days-hint"
                  className="mt-1.5 text-[11px] text-red-700/80"
                >
                  Fermé : {blockedHintText(blockedDays)}
                </p>
              )}
            </label>
            <label className="block">
              <span className={labelCls}>Heure de retrait</span>
              <select
                data-testid="order-time-input"
                value={form.time}
                onChange={(e) => {
                  setForm((f) => ({ ...f, time: e.target.value }));
                  setErrors((p) => ({ ...p, time: false }));
                }}
                disabled={!form.date || pickupClosed}
                className={`${errors.time ? fieldError : field} disabled:opacity-50`}
              >
                <option value="">
                  {form.date
                    ? pickupClosed
                      ? "Fermé ce jour — choisissez une autre date"
                      : "Choisir une heure"
                    : "Choisissez d'abord une date"}
                </option>
                {visibleSlots.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
              {hasGroupDish && (
                <p
                  data-testid="order-group-lead-hint"
                  className="mt-1.5 text-[11px] font-medium text-[#102C1F]/70"
                >
                  Plats 4 pers. et + : commande 24 h à l'avance.
                </p>
              )}
              <p
                data-testid="order-prep-note"
                className="mt-1.5 text-[11px] text-[#102C1F]/60"
              >
                Commandes en ligne à retirer à partir de demain, selon les
                horaires de retrait ci-dessus.
              </p>
            </label>
          </div>

          <div>
            <p className={labelCls}>Choisissez les plats</p>
            <p
              data-testid="order-promo-note"
              className="mb-3 inline-block rounded-full border border-[#102C1F]/25 bg-[#EDE8DE] px-3 py-1.5 text-[11px] font-medium uppercase tracking-[0.15em] text-[#102C1F]/80"
            >
              Offre spéciale : 3 Poulet grillé = 30,50 €
            </p>
            <div
              className={`rounded-sm transition-shadow duration-300 ${
                errors.dishes ? "p-1 ring-2 ring-red-500" : ""
              }`}
            >
              {dishGroups.map((g) => (
                <div key={g.cat || "sans-categorie"} className="mb-5 last:mb-0">
                  {g.cat && (
                    <p
                      data-testid={`order-category-${g.cat}`}
                      className="mb-2 text-xs uppercase tracking-[0.2em] text-[#102C1F]/50"
                    >
                      {g.cat}
                    </p>
                  )}
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                    {g.items.map((d) => {
                      const i = dishes.indexOf(d);
                      const qty = items[d.name];
                  return (
                    <div
                      key={d.name}
                      role="button"
                      tabIndex={0}
                      data-testid={`order-dish-btn-${i}`}
                      onClick={() => toggle(d)}
                      onKeyDown={(e) => e.key === "Enter" && toggle(d)}
                      className={`cursor-pointer overflow-hidden rounded-sm border text-left transition-colors duration-300 ${
                        qty
                          ? "border-[#102C1F] bg-[#102C1F] text-[#F4F1EB]"
                          : "border-[#102C1F]/25 hover:border-[#102C1F]"
                      }`}
                    >
                      {d.img && (
                        <img
                          src={d.img}
                          alt={d.name}
                          loading="lazy"
                          className="aspect-[4/3] w-full object-cover"
                        />
                      )}
                      <div className="flex items-baseline justify-between gap-1 px-2 py-1.5">
                        <span className="text-xs font-medium leading-tight">
                          {d.name}
                        </span>
                        <span className="shrink-0 font-display text-sm italic">
                          €{d.price.toFixed(2)}
                          {(d.min_qty || 1) > 1 && (
                            <span className="ml-1 text-[10px] not-italic opacity-70">
                              / pers.
                            </span>
                          )}
                        </span>
                      </div>
                      {d.desc && (
                        <p
                          data-testid={`order-dish-desc-${i}`}
                          className="px-2 pb-2 text-xs leading-snug opacity-80"
                        >
                          {d.desc}
                        </p>
                      )}
                      {qty && (
                        <div
                          className="flex items-center justify-between border-t border-[#F4F1EB]/25 px-2 py-1"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            type="button"
                            data-testid={`order-dish-minus-${i}`}
                            aria-label={`Moins ${d.name}`}
                            onClick={() =>
                              qty <= (d.min_qty || 1) ? toggle(d) : setQty(d.name, qty - 1)
                            }
                            className="rounded-full p-1 transition-colors duration-200 hover:bg-[#F4F1EB]/15"
                          >
                            <Minus size={14} />
                          </button>
                          <span
                            data-testid={`order-dish-qty-${i}`}
                            className="text-sm font-medium"
                          >
                            {qty}
                            {(d.min_qty || 1) > 1 && (
                              <span className="ml-1 text-[10px] font-normal opacity-70">
                                pers.
                              </span>
                            )}
                          </span>
                          <button
                            type="button"
                            data-testid={`order-dish-plus-${i}`}
                            aria-label={`Plus ${d.name}`}
                            onClick={() => setQty(d.name, qty + 1)}
                            className="rounded-full p-1 transition-colors duration-200 hover:bg-[#F4F1EB]/15"
                          >
                            <Plus size={14} />
                          </button>
                        </div>
                      )}
                    </div>
                    );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {Object.keys(items).length > 0 && (
            <div
              data-testid="order-summary"
              className="rounded-sm border border-[#102C1F]/15 bg-[#EDE8DE] p-4"
            >
              <p className={labelCls}>Votre commande</p>
              <ul className="space-y-1.5">
                {Object.entries(items).map(([name, qty], idx) => {
                  const dish = dishes.find((d) => d.name === name);
                  if (!dish) return null;
                  const isPromo = PROMOS[name] && qty === PROMOS[name].qty;
                  return (
                    <li
                      key={name}
                      data-testid={`order-summary-item-${idx}`}
                      className="flex items-baseline justify-between gap-3 text-sm"
                    >
                      <span className="text-[#102C1F]/85">
                        <strong className="font-semibold">
                          {qty}
                          {(dish.min_qty || 1) > 1 ? " pers." : ""} ×
                        </strong>{" "}
                        {name}
                        {isPromo && (
                          <span className="ml-2 rounded-full bg-[#102C1F] px-2 py-0.5 align-middle text-[10px] uppercase tracking-[0.12em] text-[#F4F1EB]">
                            Offre
                          </span>
                        )}
                      </span>
                      <span className="shrink-0 font-medium">
                        €{lineTotal(name, dish.price, qty).toFixed(2)}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          <label className="block">
            <span className={labelCls}>Notes</span>
            <textarea
              data-testid="order-notes-input"
              rows="2"
              placeholder="Allergies, précisions..."
              value={form.notes}
              onChange={set("notes")}
              className={field}
            />
          </label>

          <div className="flex items-center justify-between border-t border-[#102C1F]/15 pt-4">
            <span className="text-xs uppercase tracking-[0.25em] text-[#102C1F]/60">
              Total
            </span>
            <span className="text-right">
              {promoSavings > 0.001 && (
                <span
                  data-testid="order-promo-savings"
                  className="mb-1 block text-[11px] font-medium uppercase tracking-[0.15em] text-[#102C1F]/60"
                >
                  Offre 3 × Poulet grillé : −€{promoSavings.toFixed(2)}
                </span>
              )}
              <span
                data-testid="order-total"
                className="font-display text-3xl italic"
              >
                €{total.toFixed(2)}
              </span>
            </span>
          </div>

          <button
            data-testid="order-submit-btn"
            type="submit"
            disabled={sending}
            className="w-full rounded-full bg-[#102C1F] py-4 text-xs uppercase tracking-[0.25em] text-[#F4F1EB] transition-colors duration-300 hover:bg-[#1B4030] disabled:opacity-60"
          >
            {sending ? "Envoi..." : "Confirmer la commande"}
          </button>
        </motion.form>
      </main>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent
          data-testid="order-confirm-popup"
          className="border-[#102C1F]/20 bg-[#F4F1EB] text-center text-[#102C1F] sm:max-w-sm"
        >
          <DialogHeader className="items-center">
            <CheckCircle2 size={48} className="text-[#102C1F]" />
            <DialogTitle className="font-display text-3xl italic tracking-tight">
              Commande confirmée
            </DialogTitle>
            <DialogDescription className="text-sm text-[#102C1F]/65">
              Merci ! Nous vous contactons pour confirmer.
              <br />
              À emporter le{" "}
              {confirmedDate &&
                new Date(`${confirmedDate}T12:00:00`).toLocaleDateString(
                  "fr-FR",
                  { weekday: "long", day: "numeric", month: "long" }
                )}
              {confirmedTime && <> à {fmtSlot(confirmedTime)}</>} — Total : €
              {confirmedTotal.toFixed(2)}
            </DialogDescription>
          </DialogHeader>
          <button
            data-testid="order-confirm-ok-btn"
            onClick={() => setConfirmOpen(false)}
            className="mt-2 w-full rounded-full bg-[#102C1F] py-3 text-xs uppercase tracking-[0.25em] text-[#F4F1EB] transition-colors duration-300 hover:bg-[#1B4030]"
          >
            OK
          </button>
        </DialogContent>
      </Dialog>

      <Dialog open={missingOpen} onOpenChange={setMissingOpen}>
        <DialogContent
          data-testid="order-missing-popup"
          className="border-[#102C1F]/20 bg-[#F4F1EB] text-center text-[#102C1F] sm:max-w-sm"
        >
          <DialogHeader className="items-center">
            <AlertTriangle size={48} className="text-red-600" />
            <DialogTitle className="font-display text-3xl italic tracking-tight">
              Il manque quelque chose
            </DialogTitle>
            <DialogDescription className="text-sm text-[#102C1F]/65">
              Veuillez vérifier : {missingLabels.join(", ")}
            </DialogDescription>
          </DialogHeader>
          <button
            data-testid="order-missing-ok-btn"
            onClick={() => setMissingOpen(false)}
            className="mt-2 w-full rounded-full bg-[#102C1F] py-3 text-xs uppercase tracking-[0.25em] text-[#F4F1EB] transition-colors duration-300 hover:bg-[#1B4030]"
          >
            OK
          </button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
